import { useRef, useState } from "react";
import { CheckCircle2, GripVertical } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PARTS } from "@/lib/labs";

// Parts tray: drag a part onto its glowing spot in the 3D view, or just tap it (phones, keyboards and quick builders).
export const PartDock = ({ labId, parts, placed, need, shake, disabled, hint, onPick, onDrop, onDrag }) => {
  const { L } = useLang();
  const drag = useRef(null);
  const [ghost, setGhost] = useState(null);
  const down = (e, p) => {
    if (disabled || placed.includes(p) || e.button > 0) return;
    drag.current = { p, x0: e.clientX, y0: e.clientY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) > 8) { d.moved = true; onDrag(d.p); }
    if (d.moved) setGhost({ p: d.p, x: e.clientX, y: e.clientY });
  };
  const up = (e) => {
    const d = drag.current;
    drag.current = null;
    setGhost(null);
    if (!d) return;
    if (d.moved) { onDrag(null); onDrop(d.p, e.clientX, e.clientY); } else onPick(d.p);
  };
  const cancel = () => { if (drag.current?.moved) onDrag(null); drag.current = null; setGhost(null); };
  return (
    <>
      <div className="grid grid-cols-2 gap-2" data-testid="lab-parts">
        {parts.map((p) => {
          const on = placed.includes(p);
          return (
            <button key={p} type="button" disabled={on || disabled} data-testid={`lab-part-${p}`}
              onPointerDown={(e) => down(e, p)} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(p); } }}
              className={`touch-none select-none h-12 px-2.5 rounded-2xl border text-xs sm:text-sm font-bold text-left flex items-center gap-1.5 transition-[background-color,border-color,transform] active:scale-95
                ${on ? "bg-emerald-50 border-emerald-200 text-emerald-800" : hint === p && need === p ? "border-[#00B8D4] bg-[#E0FAFF] ring-2 ring-[#00E5FF]" : "bg-white border-slate-300 hover:border-[#00B8D4] cursor-grab"}
                ${shake === p ? "animate-[rs-shake_0.4s]" : ""} ${ghost?.p === p ? "opacity-40" : ""} disabled:cursor-not-allowed`}>
              {on ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <GripVertical className="w-4 h-4 shrink-0 text-slate-400" />}<span className="line-clamp-2">{L(...(PARTS[`${labId}.${p}`] || [p, p]))}</span>
            </button>
          );
        })}
      </div>
      {ghost && (
        <div className="fixed z-[200] pointer-events-none -translate-x-1/2 -translate-y-1/2 px-3 py-2 rounded-2xl bg-[#00E5FF] text-[#0A192F] text-xs font-extrabold shadow-[0_0_30px_rgba(0,229,255,0.8)]"
          style={{ left: ghost.x, top: ghost.y }} data-testid="lab-drag-ghost">{L(...(PARTS[`${labId}.${ghost.p}`] || [ghost.p, ghost.p]))}</div>
      )}
    </>
  );
};
